import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orderCategories } from '@mitang/shared';
import { listenCategories } from '../lib/repo';
import { useAppConfig } from './useAppConfig';

const KEY = ['categories'] as const;

/**
 * Dynamic category tabs (feature #4).
 *
 * QUOTA-FRIENDLY PATH: the sync publishes the tab list to
 * `app_config/global.categories`, which the app already listens to — so tabs
 * cost ONE doc read instead of scanning every product. The product-scan
 * listener remains only as a fallback for configs written before the sync
 * started publishing the list.
 *
 * ORDER: the returned list is already in the admin's tab order
 * (`app_config/global.categoryOrder`, see orderCategories) — so the first tab,
 * which HomeScreen selects by default, follows it too. No saved order ⇒
 * alphabetical, as before.
 */
export function useCategories(enabled: boolean): {
  categories: string[];
  isLoading: boolean;
  error: Error | null;
} {
  const config = useAppConfig();
  const fromConfig = config?.categories;
  const hasConfigList = Array.isArray(fromConfig) && fromConfig.length > 0;

  const qc = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  // Fallback: derive categories from products only while the config doc
  // doesn't carry the list yet.
  useEffect(() => {
    if (!enabled || hasConfigList) return;
    setError(null);
    const unsub = listenCategories(
      (cats) => {
        setError(null);
        qc.setQueryData<string[]>(KEY, cats);
      },
      (err) => setError(err),
    );
    return () => unsub();
  }, [enabled, hasConfigList, qc]);

  const q = useQuery<string[]>({
    queryKey: KEY,
    queryFn: () => qc.getQueryData<string[]>(KEY) ?? [],
    enabled: enabled && !hasConfigList,
  });

  const order = config?.categoryOrder;
  const raw = hasConfigList ? fromConfig : q.data;
  // Memoized on the inputs so consumers' effects keyed on `categories` don't
  // re-run on every render.
  const categories = useMemo(() => orderCategories(raw ?? [], order), [raw, order]);

  if (hasConfigList) {
    return { categories, isLoading: false, error: null };
  }
  return {
    categories,
    isLoading: enabled && q.data === undefined && error === null,
    error,
  };
}
